import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LuArrowLeft,
  LuArrowUpLeft,
  LuClock,
  LuSearch,
  LuTrendingUp,
  LuX,
  LuChevronRight,
} from "react-icons/lu";
import MicButton from "./ci/MicButton";
import {
  useCreateSearchQueryMutation,
  useGetTopSearchesQuery,
  useSearchDataMutation,
} from "../Redux/Api/priceAPI";
import { useAllCategoriesQuery } from "../Redux/Api/Category";
import { clearResults, clearSubResults, setSearch, setSubSearch } from "../Redux/reducer/Filter";
import { setAllCategories } from "../Redux/reducer/toStoreValues";
import { RootState } from "../Redux/store";
import { chawlaispat } from "../Redux/apiRoutes";
import usePageMeta from "./hooks/usePageMeta";
import useDebounce from "./hooks/useDebounce";
import { useCiT } from "./ci/i18n";
import { CiChip } from "./ci/Ui";
import { categoryPath } from "../utils/categoryPath";
import { pickChipCategories, titleCase } from "../screens/Components/Navbar";
import PopularProducts from "../screens/Components/PopularProducts";
import { Category, SearchQuery } from "../screens/Components/types";

/**
 * Mobile full-screen search (/searchpage) — bottom nav "Search" aur mobile header
 * ka search box yahi kholte hain, isliye ye sabse zyada use hone wali screen hai.
 *
 * Khaali: recent searches (is phone par, localStorage) · top searches · category
 * chips · popular products (wahi Home wala component, rate + Add ke saath).
 * Type karte hi: "Search 'x'" row · matching categories · suggestions (poori
 * height, 48px rows, highlight) · loading skeleton · no-result par "phir bhi
 * search karo". Har action `goSearch()` se — term set → /items.
 */

const RECENT_KEY = "ci_recent_searches";
const RECENT_MAX = 8;

const readRecent = (): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(v) ? v.filter((s) => typeof s === "string" && s.trim()).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
};

const writeRecent = (list: string[]) => {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    /* private mode etc. — recent optional hai */
  }
};

interface Suggestion {
  _id?: string;
  itemName?: string;
  name?: string;
}

export const SearchPage = () => {
  const t = useCiT();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const [search, setSearchText] = useState<string>("");
  const [recent, setRecent] = useState<string[]>(() => readRecent());
  const [results, setResults] = useState<Suggestion[]>([]);

  const [searchAllProducts, { data: searchResponse, isLoading }] = useSearchDataMutation();
  const [createSearchQuery] = useCreateSearchQueryMutation();
  const { data: queryData } = useGetTopSearchesQuery();

  // Categories — desktop Navbar store bharta hai; mobile par ye page khud bhare.
  const allCategories = useSelector((state: RootState) => state.values.allCategories as Category[]);
  const { data: categoriesData } = useAllCategoriesQuery();
  useEffect(() => {
    if (categoriesData?.data && !(Array.isArray(allCategories) && allCategories.length)) {
      dispatch(setAllCategories(categoriesData.data));
    }
  }, [categoriesData, allCategories, dispatch]);

  const q = search.trim();
  const ql = q.toLowerCase();
  const debounced = useDebounce(q, 350);

  useEffect(() => {
    setResults(Array.isArray(searchResponse) ? searchResponse : []);
  }, [searchResponse]);

  const fetchSuggestions = useCallback(
    async (term: string) => {
      if (!term) {
        setResults([]);
        return;
      }
      try {
        await searchAllProducts(term);
      } catch (e) {
        // suggestions optional — search button phir bhi kaam karta hai
      }
    },
    [searchAllProducts]
  );

  useEffect(() => {
    fetchSuggestions(debounced);
  }, [debounced, fetchSuggestions]);

  /** Ek hi jagah se: term set → /items. Recent me bhi save. */
  const goSearch = (term: string) => {
    const v = (term || "").trim();
    if (!v) {
      inputRef.current?.focus();
      return;
    }
    const next = [v, ...recent.filter((r) => r.toLowerCase() !== v.toLowerCase())].slice(0, RECENT_MAX);
    setRecent(next);
    writeRecent(next);
    dispatch(setSearch(v));
    createSearchQuery({ query: v });
    navigate("/items");
  };

  const openCategory = (c: Category) => {
    dispatch(clearResults());
    navigate(categoryPath(c), { state: { id: c.id } });
  };

  const openSub = (name: string) => {
    navigate("/subItems");
    dispatch(clearSubResults());
    dispatch(setSubSearch(name));
  };

  const onSuggestion = (index: number, s: Suggestion) => {
    // Backend: pehle 3 = poore product (itemName), baaki = sub-item (name)
    if (index < 3) goSearch(s.itemName || s.name || q);
    else openSub(s.name || s.itemName || q);
  };

  const clearRecent = () => {
    setRecent([]);
    writeRecent([]);
  };

  const chipCats = useMemo(() => pickChipCategories(allCategories), [allCategories]);
  const matchingCats = useMemo(() => {
    const list = Array.isArray(allCategories) ? allCategories : [];
    if (!ql) return [];
    return list.filter((c) => String(c.name || "").toLowerCase().includes(ql)).slice(0, 4);
  }, [allCategories, ql]);

  const topSearches: string[] = useMemo(() => {
    const list: SearchQuery[] = queryData?.topSearches || [];
    return list.map((s) => String(s?.query || "").trim()).filter(Boolean).slice(0, 8);
  }, [queryData]);

  usePageMeta(
    "Search | Building Material Supplier | Chawla Ispat",
    "Explore search results and find products or information quickly on the Search Page of Chawla Ispat.",
    chawlaispat
  );

  const highlight = (text: string) => {
    if (!ql) return <span>{text}</span>;
    const safe = ql.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${safe})`, "gi"));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === ql ? (
            <mark key={i} className="rounded-sm bg-[#ffe9a8] px-0.5 text-ci-ink">
              {part}
            </mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  };

  const row =
    "flex min-h-[48px] w-full items-center gap-3 px-4 text-left text-[15px] text-ci-ink active:bg-ci-bg";
  const head =
    "flex items-center justify-between px-4 pb-1.5 pt-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-ci-muted";

  return (
    <div className="min-h-[100dvh] w-full max-w-full overflow-x-hidden bg-white pb-24">
      {/* ── Sticky search bar ─────────────────────────────────────── */}
      <form
        className="sticky top-0 z-20 flex items-center gap-1.5 border-b border-ci-line bg-white px-2 py-2 shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          inputRef.current?.blur();
          goSearch(q);
        }}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ci-ink active:bg-ci-bg"
        >
          <LuArrowLeft size={22} />
        </button>

        <div className="flex h-12 min-w-0 flex-1 items-center gap-1 rounded-full border-[1.5px] border-ci-navy bg-white pl-3 pr-1">
          <LuSearch size={18} className="shrink-0 text-ci-muted" />
          <input
            ref={inputRef}
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            className="h-full min-w-0 flex-1 border-none bg-transparent text-[16px] text-ci-ink outline-none placeholder:text-ci-muted"
            placeholder={t("TMT, GC sheet, 2x1 pipe — size ya brand bhi")}
            value={search}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {search ? (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => {
                setSearchText("");
                setResults([]);
                inputRef.current?.focus();
              }}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ci-muted active:bg-ci-bg"
            >
              <LuX size={18} />
            </button>
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center">
              <MicButton size={20} onResult={(text) => setSearchText(text)} />
            </span>
          )}
        </div>

        <button
          type="submit"
          aria-label="Search"
          className="flex h-12 shrink-0 items-center justify-center rounded-full bg-ci-orange px-4 text-[15px] font-semibold text-white active:bg-ci-orange2"
        >
          {t("Search")}
        </button>
      </form>

      {/* ── Empty state: recent · top · categories · popular ───────── */}
      {!q ? (
        <div>
          {recent.length ? (
            <section>
              <div className={head}>
                <span className="flex items-center gap-1.5">
                  <LuClock size={13} /> {t("Recent searches")}
                </span>
                <button
                  type="button"
                  onClick={clearRecent}
                  className="text-[12px] font-medium normal-case tracking-normal text-ci-navy"
                >
                  {t("Clear")}
                </button>
              </div>
              <div className="flex flex-wrap gap-2 px-4 pb-1">
                {recent.map((r) => (
                  <CiChip key={r} onClick={() => goSearch(r)}>
                    <LuClock size={13} className="text-ci-muted" /> {r}
                  </CiChip>
                ))}
              </div>
            </section>
          ) : null}

          {topSearches.length ? (
            <section>
              <div className={head}>
                <span className="flex items-center gap-1.5">
                  <LuTrendingUp size={13} /> {t("Top searches")}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 px-4 pb-1">
                {topSearches.map((s) => (
                  <CiChip key={s} onClick={() => goSearch(s)}>
                    {s}
                  </CiChip>
                ))}
              </div>
            </section>
          ) : null}

          {chipCats.length ? (
            <section>
              <div className={head}>
                <span>{t("Categories")}</span>
                <button
                  type="button"
                  onClick={() => navigate("/categories")}
                  className="flex items-center gap-0.5 text-[12px] font-medium normal-case tracking-normal text-ci-navy"
                >
                  {t("All categories")} <LuChevronRight size={14} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 px-4 pb-1">
                {chipCats.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => openCategory(c)}
                    className="flex min-h-[48px] items-center justify-between gap-2 rounded-lg border border-ci-line bg-white px-3 text-left text-[14px] font-medium text-ci-ink active:bg-ci-bg"
                  >
                    <span className="truncate">{titleCase(c.name)}</span>
                    <span className="shrink-0 text-[12px] text-ci-muted">{c.itemCount || ""}</span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          <p className="px-4 pb-2 pt-4 text-[13px] text-ci-muted">
            {t('Size/brand bhi likh sakte hain — "12mm kamdhenu", "2x1 pipe", "ultratech 43".')}
          </p>

          <div className="px-4 pt-2">
            <PopularProducts />
          </div>
        </div>
      ) : (
        /* ── Typing: search row · categories · suggestions ─────────── */
        <div className="flex flex-col py-1">
          <button type="button" className={`${row} font-semibold`} onClick={() => goSearch(q)}>
            <LuSearch size={18} className="shrink-0 text-ci-navy" />
            <span className="truncate">
              {t("Search")} "<span className="text-ci-navy">{q}</span>"
            </span>
            <LuChevronRight size={16} className="ml-auto shrink-0 text-ci-muted" />
          </button>

          {matchingCats.length ? (
            <div className="flex gap-2 overflow-x-auto px-4 py-2 [scrollbar-width:none]">
              {matchingCats.map((c) => (
                <CiChip key={c.id} onClick={() => openCategory(c)}>
                  {titleCase(c.name)} <span className="text-ci-muted">· {t("category")}</span>
                </CiChip>
              ))}
            </div>
          ) : null}

          {isLoading && !results.length ? (
            <div className="flex flex-col gap-3 px-4 py-3" aria-busy="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-5 w-5 rounded bg-ci-bg" />
                  <div
                    className="h-4 flex-1 animate-pulse rounded bg-ci-bg"
                    style={{ maxWidth: `${70 - i * 8}%` }}
                  />
                </div>
              ))}
            </div>
          ) : results.length ? (
            <ul className="divide-y divide-ci-line border-t border-ci-line">
              {results.slice(0, 12).map((s, index) => {
                const label = index < 3 ? s.itemName || s.name || "" : s.name || s.itemName || "";
                if (!label) return null;
                return (
                  <li key={`${s._id || label}-${index}`}>
                    <button type="button" className={row} onClick={() => onSuggestion(index, s)}>
                      {index < 3 ? (
                        <LuSearch size={18} className="shrink-0 text-ci-muted" />
                      ) : (
                        <LuArrowUpLeft size={18} className="shrink-0 text-ci-muted" />
                      )}
                      <span className="line-clamp-2 leading-snug">{highlight(label)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
              <LuSearch size={28} className="text-ci-muted" />
              <p className="text-[14px] text-ci-muted">
                {t("Is naam par suggestion nahi mila — spelling badal kar dekhein, ya seedha search karein.")}
              </p>
              <button
                type="button"
                onClick={() => goSearch(q)}
                className="h-11 rounded-md bg-ci-navy px-5 text-[15px] font-semibold text-white"
              >
                {t("Phir bhi search karo")} "{q}"
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchPage;
